import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import { FormNavigationProvider, NavTextField } from '../components/FormNavigation';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import DashboardLayout from '../components/DashboardLayout';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchRoles, addRole, removeRole, updateRole } from '../store/rolesSlice';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import type { Role, CreateRolePayload } from '../types/roles';

const SYSTEM_ADMIN_ID = 'ROLE#system-admin';

function shortId(fullId: string): string {
  return fullId.startsWith('ROLE#') ? fullId.slice(5) : fullId;
}

/** Derives a URL-friendly slug from a human-readable name. */
function generateId(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// ─── Permission checklist section ────────────────────────────────────────────

interface PermChecklistProps {
  label: string;
  permissions: string[];
  selected: Set<string>;
  onChange: (perm: string, checked: boolean) => void;
}

function PermChecklist({ label, permissions, selected, onChange }: PermChecklistProps) {
  if (permissions.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <FormGroup>
        {permissions.map((p) => (
          <FormControlLabel
            key={p}
            control={
              <Checkbox
                size="small"
                checked={selected.has(p)}
                onChange={(e) => onChange(p, e.target.checked)}
              />
            }
            label={<Typography variant="body2">{p}</Typography>}
          />
        ))}
      </FormGroup>
    </Box>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type AssignablePermissions = { view: string[]; read: string[]; modify: string[]; remove: string[]; create: string[] } | null;

function allAssignable(ap: AssignablePermissions): Set<string> {
  if (!ap) return new Set();
  return new Set([...ap.view, ...ap.read, ...ap.modify, ...ap.remove, ...ap.create]);
}

// ─── Shared permission list box ───────────────────────────────────────────────

function PermissionListBox({ assignablePermissions, selectedPerms, togglePerm }: {
  assignablePermissions: Exclude<AssignablePermissions, null>;
  selectedPerms: Set<string>;
  togglePerm: (perm: string, checked: boolean) => void;
}) {
  return (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions</Typography>
      <Divider sx={{ mb: 1 }} />
      <Box
        sx={{
          height: 280,
          overflowY: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          px: 1.5,
          py: 1,
        }}
      >
        <PermChecklist label="View" permissions={assignablePermissions.view} selected={selectedPerms} onChange={togglePerm} />
        <PermChecklist label="Read" permissions={assignablePermissions.read} selected={selectedPerms} onChange={togglePerm} />
        <PermChecklist label="Modify" permissions={assignablePermissions.modify} selected={selectedPerms} onChange={togglePerm} />
        <PermChecklist label="Remove" permissions={assignablePermissions.remove} selected={selectedPerms} onChange={togglePerm} />
        <PermChecklist label="Create" permissions={assignablePermissions.create} selected={selectedPerms} onChange={togglePerm} />
      </Box>
    </>
  );
}

// ─── Create / Clone dialog ────────────────────────────────────────────────────

interface CreateDialogProps {
  open: boolean;
  cloneSource: Role | null;
  onClose: () => void;
  onCreated: (role: Role) => void;
}

function CreateRoleDialog({ open, cloneSource, onClose, onCreated }: CreateDialogProps) {
  const assignablePermissions = useAppSelector((s) => s.roles.assignablePermissions);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  // Initialise fields when dialog opens
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    if (cloneSource) {
      setName(`Copy of ${cloneSource.Name}`);
      setDescription(cloneSource.Description ?? '');
      const assignable = allAssignable(assignablePermissions);
      const pre = new Set(cloneSource.Permissions.filter((p) => assignable.has(p)));
      setSelectedPerms(pre);
    } else {
      setName('');
      setDescription('');
      setSelectedPerms(new Set());
    }
  }, [open, cloneSource, assignablePermissions]);

  const derivedId = generateId(name);

  function togglePerm(perm: string, checked: boolean) {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      checked ? next.add(perm) : next.delete(perm);
      return next;
    });
  }

  async function handleSubmit() {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!derivedId) { setError('Could not generate a valid ID from the name. Please use alphanumeric characters.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const payload: CreateRolePayload = {
        id: derivedId,
        name: name.trim(),
        description: description.trim(),
        permissions: Array.from(selectedPerms),
      };
      const response = await apiService.createRole(payload);
      onCreated(response.data);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create role');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>{cloneSource ? `Clone Role` : 'Create New Role'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <NavTextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            size="small"
            sx={{ mb: 2 }}
          />
          <NavTextField
            label="ID (auto-generated)"
            value={derivedId}
            fullWidth
            size="small"
            disabled
            sx={{ mb: 2 }}
            helperText="Automatically derived from the role name"
          />
          <NavTextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            sx={{ mb: 3 }}
          />

          {assignablePermissions ? (
            <PermissionListBox
              assignablePermissions={assignablePermissions}
              selectedPerms={selectedPerms}
              togglePerm={togglePerm}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">Loading permissions…</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={18} /> : cloneSource ? 'Clone' : 'Create'}
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ─── Edit dialog ──────────────────────────────────────────────────────────────

interface EditDialogProps {
  roleShortId: string | null;
  onClose: () => void;
  onUpdated: (role: Role) => void;
}

function EditRoleDialog({ roleShortId, onClose, onUpdated }: EditDialogProps) {
  const assignablePermissions = useAppSelector((s) => s.roles.assignablePermissions);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!roleShortId) return;
    setLoading(true);
    setError(null);
    setName('');
    setDescription('');
    setSelectedPerms(new Set());
    apiService
      .getRole(roleShortId)
      .then((res) => {
        const role = res.data;
        setName(role.Name);
        setDescription(role.Description ?? '');
        const assignable = allAssignable(assignablePermissions);
        setSelectedPerms(new Set(role.Permissions.filter((p) => assignable.has(p))));
      })
      .catch((err) => setError(err?.message ?? 'Failed to load role'))
      .finally(() => setLoading(false));
  }, [roleShortId]); // eslint-disable-line react-hooks/exhaustive-deps

  function togglePerm(perm: string, checked: boolean) {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      checked ? next.add(perm) : next.delete(perm);
      return next;
    });
  }

  async function handleSubmit() {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!roleShortId) return;
    setError(null);
    setSubmitting(true);
    try {
      const response = await apiService.updateRole(roleShortId, {
        name: name.trim(),
        description: description.trim(),
        permissions: Array.from(selectedPerms),
      });
      onUpdated(response.data);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={!!roleShortId}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Edit Role</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <NavTextField
                label="ID"
                value={roleShortId ?? ''}
                fullWidth
                size="small"
                disabled
                sx={{ mb: 2 }}
                helperText="Role ID cannot be changed"
              />
              <NavTextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                size="small"
                sx={{ mb: 2 }}
              />
              <NavTextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={2}
                sx={{ mb: 3 }}
              />
              {assignablePermissions ? (
                <PermissionListBox
                  assignablePermissions={assignablePermissions}
                  selectedPerms={selectedPerms}
                  togglePerm={togglePerm}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">Loading permissions…</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting || loading}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting || loading}>
            {submitting ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
}

// ─── Delete confirmation dialog ───────────────────────────────────────────────

interface DeleteDialogProps {
  role: Role | null;
  onClose: () => void;
  onDeleted: (id: string) => void;
}

function DeleteRoleDialog({ role, onClose, onDeleted }: DeleteDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!role) { setError(null); setDeleting(false); }
  }, [role]);

  async function handleConfirm() {
    if (!role) return;
    setDeleting(true);
    setError(null);
    try {
      await apiService.deleteRole(shortId(role.ID));
      onDeleted(role.ID);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete role');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Dialog open={!!role} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Role</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <DialogContentText>
          Are you sure you want to delete <strong>{role?.Name}</strong>? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={deleting}>
          {deleting ? <CircularProgress size={18} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Permissions view dialog ──────────────────────────────────────────────────

function PermissionsViewDialog({ role, onClose }: { role: Role | null; onClose: () => void }) {
  if (!role) return null;

  const groups: Record<string, string[]> = { View: [], Read: [], Modify: [], Remove: [], Create: [], Other: [] };
  for (const perm of role.Permissions ?? []) {
    if (perm.startsWith('VIEW_')) groups.View.push(perm);
    else if (perm.startsWith('READ_')) groups.Read.push(perm);
    else if (perm.startsWith('MODIFY_')) groups.Modify.push(perm);
    else if (perm.startsWith('REMOVE_')) groups.Remove.push(perm);
    else if (perm.startsWith('CREATE_') || perm.startsWith('CLONE_')) groups.Create.push(perm);
    else groups.Other.push(perm);
  }

  return (
    <Dialog open={!!role} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{role.Name} — Permissions ({role.Permissions?.length ?? 0})</DialogTitle>
      <DialogContent dividers>
        {(!role.Permissions || role.Permissions.length === 0) ? (
          <Typography variant="body2" color="text.secondary">No permissions assigned.</Typography>
        ) : (
          Object.entries(groups)
            .filter(([, perms]) => perms.length > 0)
            .map(([label, perms]) => (
              <Box key={label} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
                <Divider sx={{ mb: 1 }} />
                {perms.map((p) => (
                  <Typography key={p} variant="body2" sx={{ fontFamily: 'monospace', py: 0.25 }}>{p}</Typography>
                ))}
              </Box>
            ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const UserRoles: React.FC = () => {
  const dispatch = useAppDispatch();
  const { roles, isLoading, error } = useAppSelector((s) => s.roles);

  const [createOpen, setCreateOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState<Role | null>(null);
  const [editShortId, setEditShortId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [permViewRole, setPermViewRole] = useState<Role | null>(null);


  function handleRefresh() {
    dispatch(fetchRoles());
  }

  function openCreate() {
    setCloneSource(null);
    setCreateOpen(true);
  }

  function openClone(role: Role) {
    setCloneSource(role);
    setCreateOpen(true);
  }

  function handleCreated(role: Role) {
    dispatch(addRole(role));
    dispatch(showNotification({ message: `Role "${role.Name}" created successfully`, severity: 'success' }));
  }

  function handleUpdated(role: Role) {
    dispatch(updateRole(role));
    dispatch(showNotification({ message: `Role "${role.Name}" updated successfully`, severity: 'success' }));
  }

  function handleDeleted(id: string) {
    dispatch(removeRole(id));
    dispatch(showNotification({ message: 'Role deleted successfully', severity: 'success' }));
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Role Management
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={isLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Create Role
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage roles and permissions for your unit.
      </Typography>

      {/* Error banner */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Permissions</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && roles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : roles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">No roles found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              roles.map((role) => {
                const isSystemAdmin = role.ID === SYSTEM_ADMIN_ID;
                const isSystem = role.Type === 'SYSTEM';
                return (
                  <TableRow key={role.ID} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{role.Name}</Typography>
                      <Typography variant="caption" color="text.secondary">{shortId(role.ID)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{role.Description || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.Type}
                        size="small"
                        color={isSystem ? 'default' : 'primary'}
                        variant={isSystem ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Link
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => setPermViewRole(role)}
                      >
                        {role.Permissions?.length ?? 0}
                      </Link>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={isSystem ? 'System roles cannot be edited' : 'Edit role'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setEditShortId(shortId(role.ID))}
                            disabled={isSystem}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isSystemAdmin ? 'System Admin cannot be cloned' : 'Clone role'}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openClone(role)}
                            disabled={isSystemAdmin}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isSystem ? 'System roles cannot be deleted' : 'Delete role'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteTarget(role)}
                            disabled={isSystem}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialogs */}
      <CreateRoleDialog
        open={createOpen}
        cloneSource={cloneSource}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
      <EditRoleDialog
        roleShortId={editShortId}
        onClose={() => setEditShortId(null)}
        onUpdated={handleUpdated}
      />
      <DeleteRoleDialog
        role={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />
      <PermissionsViewDialog
        role={permViewRole}
        onClose={() => setPermViewRole(null)}
      />

    </DashboardLayout>
  );
};

export default UserRoles;
